/**
 * DataManagerImagePoint.ts
 *
 * Manages image points extracted from Construct 3 sprites.
 * Image points are named positions within a sprite frame (e.g., WeaponCenter, TractorBeam).
 * They are stored as normalized coordinates (0-1) relative to the frame size.
 */
import { Entity } from '../entities/base/Entity';

export interface ImagePoint {
  name: string;
  x: number; // Normalized 0-1
  y: number; // Normalized 0-1
}

export interface FrameImagePoints {
  frameIndex: number;
  width: number;
  height: number;
  originX: number;
  originY: number;
  imagePoints: ImagePoint[];
}

export interface AnimationImagePoints {
  animationName: string;
  frames: FrameImagePoints[];
}

export interface SpriteImagePoints {
  spriteName: string;
  animations: AnimationImagePoints[];
}

/**
 * DataManagerImagePoint - Loads and provides access to image points data
 *
 * Uses base + override pattern:
 * - imagepoints-c3data.json: Auto-generated from C3 (SSOT)
 * - imagepoints-overrides.json: Manual adjustments
 */
export class DataManagerImagePoint {
  private sprites: Map<string, SpriteImagePoints> = new Map();
  private loaded = false;

  /**
   * Load image points data from JSON cache (c3data + overrides)
   */
  load(scene: Phaser.Scene): void {
    if (this.loaded) return;

    try {
      // Load base data from C3 extraction
      const c3data = scene.cache.json.get('imagepoints-c3data');
      if (c3data && Array.isArray(c3data.sprites)) {
        for (const sprite of c3data.sprites) {
          this.sprites.set(Entity.normalize(sprite.spriteName), this.cloneSprite(sprite));
        }
      }

      // Load and merge overrides
      const overrides = scene.cache.json.get('imagepoints-overrides');
      if (overrides && Array.isArray(overrides.sprites)) {
        for (const overrideSprite of overrides.sprites) {
          const normalizedName = Entity.normalize(overrideSprite.spriteName);
          const existing = this.sprites.get(normalizedName);
          if (existing) {
            // Merge override into existing
            this.mergeSprite(existing, overrideSprite);
          } else {
            // New sprite from overrides
            this.sprites.set(normalizedName, this.cloneSprite(overrideSprite));
          }
        }
      }

      this.loaded = true;
      // console.log(`[DataManagerImagePoint] Loaded ${this.sprites.size} sprites`);
    } catch (error) {
      console.error('[DataManagerImagePoint] Failed to load image points:', error);
    }
  }

  /**
   * Deep clone a sprite to avoid mutating source data
   */
  private cloneSprite(sprite: SpriteImagePoints): SpriteImagePoints {
    return {
      spriteName: sprite.spriteName,
      animations: sprite.animations.map(anim => ({
        animationName: anim.animationName,
        frames: anim.frames.map(frame => ({
          ...frame,
          imagePoints: frame.imagePoints.map(p => ({ ...p })),
        })),
      })),
    };
  }

  /**
   * Merge override sprite data into existing sprite
   * Overrides take precedence at the imagePoint level
   */
  private mergeSprite(target: SpriteImagePoints, override: SpriteImagePoints): void {
    for (const overrideAnim of override.animations) {
      const targetAnim = target.animations.find(
        a => Entity.matches(a.animationName, overrideAnim.animationName)
      );

      if (targetAnim) {
        // Merge frames
        for (const overrideFrame of overrideAnim.frames) {
          const targetFrame = targetAnim.frames.find(f => f.frameIndex === overrideFrame.frameIndex);
          if (targetFrame) {
            // Override frame properties
            targetFrame.width = overrideFrame.width;
            targetFrame.height = overrideFrame.height;
            targetFrame.originX = overrideFrame.originX;
            targetFrame.originY = overrideFrame.originY;
            // Merge imagePoints (override by name)
            for (const overridePoint of overrideFrame.imagePoints) {
              const existingPointIndex = targetFrame.imagePoints.findIndex(
                p => Entity.matches(p.name, overridePoint.name)
              );
              if (existingPointIndex >= 0) {
                targetFrame.imagePoints[existingPointIndex] = { ...overridePoint };
              } else {
                targetFrame.imagePoints.push({ ...overridePoint });
              }
            }
          } else {
            // Add new frame
            targetAnim.frames.push({
              ...overrideFrame,
              imagePoints: overrideFrame.imagePoints.map(p => ({ ...p })),
            });
          }
        }
      } else {
        // Add new animation
        target.animations.push({
          animationName: overrideAnim.animationName,
          frames: overrideAnim.frames.map(frame => ({
            ...frame,
            imagePoints: frame.imagePoints.map(p => ({ ...p })),
          })),
        });
      }
    }
  }

  /**
   * Get image point for a specific sprite, animation, and frame
   */
  getImagePoint(
    spriteName: string,
    animationName: string,
    frameIndex: number,
    pointName: string
  ): ImagePoint | undefined {
    const sprite = this.sprites.get(Entity.normalize(spriteName));
    if (!sprite) return undefined;

    // Try to find the requested animation
    let animation = sprite.animations.find(
      (a) => Entity.matches(a.animationName, animationName)
    );

    // Fallback to "default" if animation not found
    if (!animation) {
      animation = sprite.animations.find(
        (a) => Entity.matches(a.animationName, 'default')
      );
    }
    if (!animation) return undefined;

    // Clamp frame index to available frames
    const frame = animation.frames[Math.min(frameIndex, animation.frames.length - 1)];
    if (!frame) return undefined;

    return frame.imagePoints.find((p) => Entity.matches(p.name, pointName));
  }

  /**
   * Get frame data (origin, size) for calculations
   */
  getFrameData(
    spriteName: string,
    animationName: string,
    frameIndex: number
  ): FrameImagePoints | undefined {
    const sprite = this.sprites.get(Entity.normalize(spriteName));
    if (!sprite) return undefined;

    // Try to find the requested animation
    let animation = sprite.animations.find(
      (a) => Entity.matches(a.animationName, animationName)
    );

    // Fallback to "default" if animation not found
    if (!animation) {
      animation = sprite.animations.find(
        (a) => Entity.matches(a.animationName, 'default')
      );
    }
    if (!animation) return undefined;

    return animation.frames[Math.min(frameIndex, animation.frames.length - 1)];
  }

  /**
   * Calculate world position of an image point given sprite state
   *
   * Uses origin from frameData (JSON), NOT from sprite, because:
   * - ImagePoints were extracted from C3 with a specific origin per frame
   * - Phaser sprite origin may have been modified by EntityFactory
   *
   * @param spriteName - Name of the sprite (e.g., 'player')
   * @param animationName - Current animation name (e.g., 'default')
   * @param frameIndex - Current frame index
   * @param pointName - Name of the image point (e.g., 'WeaponCenter')
   * @param worldX - Sprite's world X position
   * @param worldY - Sprite's world Y position
   * @param displayWidth - Sprite's display width (scaled)
   * @param displayHeight - Sprite's display height (scaled)
   * @param _originX - Ignored - use frameData.originX instead
   * @param _originY - Ignored - use frameData.originY instead
   * @param rotation - Sprite's rotation in radians (default: 0)
   * @returns World position {x, y} or undefined if point not found
   */
  getWorldPosition(
    spriteName: string,
    animationName: string,
    frameIndex: number,
    pointName: string,
    worldX: number,
    worldY: number,
    displayWidth: number,
    displayHeight: number,
    _originX?: number, // Ignored - use frameData.originX instead
    _originY?: number, // Ignored - use frameData.originY instead
    rotation: number = 0
  ): { x: number; y: number } | undefined {
    const frameData = this.getFrameData(spriteName, animationName, frameIndex);
    if (!frameData) {
      if (!this.sprites.has(Entity.normalize(spriteName))) {
        console.warn(`[DataManagerImagePoint] Sprite "${spriteName}" not found`);
      }
      return undefined;
    }

    const point = frameData.imagePoints.find(p => Entity.matches(p.name, pointName));
    if (!point) {
      return undefined;
    }

    // Calculate offset using origin from frameData (C3 extraction)
    let offsetX = (point.x - frameData.originX) * displayWidth;
    let offsetY = (point.y - frameData.originY) * displayHeight;

    // Apply sprite rotation if provided
    if (rotation !== 0) {
      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);
      const rotatedX = offsetX * cos - offsetY * sin;
      const rotatedY = offsetX * sin + offsetY * cos;
      offsetX = rotatedX;
      offsetY = rotatedY;
    }

    return {
      x: worldX + offsetX,
      y: worldY + offsetY,
    };
  }

  /**
   * Get all image points with world positions for a sprite (for debug visualization)
   *
   * Uses origin from frameData (JSON), NOT from sprite, because:
   * - ImagePoints were extracted from C3 with a specific origin per frame
   * - Phaser sprite origin may have been modified by EntityFactory
   */
  getAllWorldPositions(
    spriteName: string,
    animationName: string,
    frameIndex: number,
    worldX: number,
    worldY: number,
    displayWidth: number,
    displayHeight: number,
    _originX: number, // Ignored - use frameData.originX instead
    _originY: number  // Ignored - use frameData.originY instead
  ): Array<{ name: string; x: number; y: number }> {
    const frameData = this.getFrameData(spriteName, animationName, frameIndex);
    if (!frameData) return [];

    const result: Array<{ name: string; x: number; y: number }> = [];
    for (const point of frameData.imagePoints) {
      // Use origin from frameData (C3 extraction), not from Phaser sprite
      const offsetX = (point.x - frameData.originX) * displayWidth;
      const offsetY = (point.y - frameData.originY) * displayHeight;
      result.push({
        name: point.name,
        x: worldX + offsetX,
        y: worldY + offsetY,
      });
    }
    return result;
  }

  /**
   * Check if sprite has image points data
   */
  hasSprite(spriteName: string): boolean {
    return this.sprites.has(Entity.normalize(spriteName));
  }

  /**
   * Get all point names available for a sprite
   */
  getPointNames(spriteName: string): string[] {
    const sprite = this.sprites.get(Entity.normalize(spriteName));
    if (!sprite || sprite.animations.length === 0) return [];

    const firstFrame = sprite.animations[0]?.frames[0];
    if (!firstFrame) return [];

    return firstFrame.imagePoints.map((p) => p.name);
  }
}
